import { Container, Title, Text, Button, Group, TextInput, Paper, Stack, Badge, CopyButton, ActionIcon, Tooltip, SimpleGrid, Box } from '@mantine/core';
import { IconRefresh, IconCopy, IconCheck, IconGhost, IconDeviceGamepad, IconWand, IconUsers, IconPalette } from '@tabler/icons-react';
import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import SEO from '../layout/SEO';
import FAQ from '../info/FAQ';
import CyberBackground from '../landing/CyberBackground';
import { NAME_DATA, GLYPH_OPTIONS } from '../../data/textTools';
import { NAME_GEN_FAQ } from '../../data/faqs';

/**
 * NameGenerator component provides a tool to generate and style Kahoot nicknames.
 * It features a live lobby simulator to preview how the names will appear in-game.
 */
export default function NameGenerator() {
    const [generatedName, setGeneratedName] = useState(null);
    const [lobbyNames, setLobbyNames] = useState(["Teacher", "Alex", "Sarah"]);
    const [mode, setMode] = useState('funny');
    const [hasGenerated, setHasGenerated] = useState(false);
    const inputRef = useRef(null);

    /**
     * Generates a new name based on the selected mode and adds it to the lobby preview.
     */
    const generateName = () => {
        const list = NAME_DATA[mode];
        const random = list[Math.floor(Math.random() * list.length)];

        setGeneratedName(random);
        setHasGenerated(true);

        setLobbyNames(prev => [random, ...prev].slice(0, 12));
    };

    /**
     * Handles clicking on a styled variant. Adds the styled name to the lobby preview.
     * @param {string} styledName - The transformed name string.
     */
    const handleStyleClick = (styledName) => {
        setLobbyNames(prev => [styledName, ...prev].slice(0, 12));
    };

    return (
        <Box bg="#050505" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <SEO
                title="Kahoot Name Generator | Glitch, Invisible & Funny Names"
                description="Generate unbannable, invisible, and glitchy Kahoot names. Preview them in a live lobby simulator."
                path="/names"
            />

            <Navbar />

            <Box style={{ flex: 1, position: 'relative' }}>
                <CyberBackground>
                    <Container size="md" py={100} style={{ position: 'relative', zIndex: 10 }}>
                        <Stack align="center" gap={60}>

                            {/* HEADER */}
                            <div style={{ textAlign: 'center' }}>
                                <Badge variant="gradient" gradient={{ from: 'pink', to: 'orange' }} size="lg" mb="sm" radius="sm">
                                    Including glitchy names & more
                                </Badge>
                                <Title order={1} size="3.5rem" fw={900} style={{ lineHeight: 1.1, letterSpacing: '-2px', textShadow: '0 0 30px rgba(230, 73, 128, 0.4)' }}>
                                    Kahoot <span style={{ color: '#e64980' }}>Name Generator</span>
                                </Title>
                                <Text c="dimmed" mt="md" size="lg" maw={500} mx="auto">
                                    Bypass filters with our stealth algorithms. Select a protocol below.
                                </Text>
                            </div>

                            {/* MAIN TOOL */}
                            <Paper
                                p="xl" radius="xl" w="100%" withBorder
                                style={{
                                    backgroundColor: 'rgba(10, 10, 10, 0.7)',
                                    backdropFilter: 'blur(20px)',
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                    boxShadow: '0 0 50px rgba(0,0,0,0.5)'
                                }}
                            >
                                <Stack gap="lg">
                                    <SimpleGrid cols={3} spacing="md">
                                        {[
                                            { id: 'funny', icon: IconDeviceGamepad, label: 'Funny' },
                                            { id: 'stealth', icon: IconGhost, label: 'Stealth' },
                                            { id: 'glitch', icon: IconWand, label: 'Glitch' }
                                        ].map((m) => (
                                            <Button
                                                key={m.id}
                                                variant={mode === m.id ? 'filled' : 'subtle'}
                                                color={mode === m.id ? 'pink' : 'gray'}
                                                onClick={() => setMode(m.id)}
                                                h={50}
                                                leftSection={<m.icon size={20} />}
                                            >
                                                {m.label}
                                            </Button>
                                        ))}
                                    </SimpleGrid>

                                    <AnimatePresence mode='wait'>
                                        {generatedName && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.9, height: 0 }}
                                                animate={{ opacity: 1, scale: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ type: "spring", bounce: 0.5 }}
                                            >
                                                <Group justify="center" gap="xs" mb="md">
                                                    <TextInput
                                                        ref={inputRef}
                                                        value={generatedName} readOnly variant="filled" size="xl" radius="md"
                                                        styles={{ input: { textAlign: 'center', fontSize: '1.8rem', fontWeight: 800, color: '#fff', backgroundColor: '#1A1A1A', border: '1px solid #333' } }}
                                                        w="100%"
                                                    />
                                                </Group>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <Group justify="center">
                                        <Button
                                            size="xl" radius="md"
                                            variant="gradient" gradient={{ from: 'pink', to: 'orange' }}
                                            leftSection={<IconRefresh size={24} />}
                                            onClick={generateName}
                                            style={{ flex: 1, boxShadow: '0 0 20px rgba(230, 73, 128, 0.3)' }}
                                        >
                                            Generate Identity
                                        </Button>

                                        {generatedName && (
                                            <CopyButton value={generatedName} timeout={2000}>
                                                {({ copied, copy }) => (
                                                    <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="right">
                                                        <ActionIcon color={copied ? 'teal' : 'gray'} variant="light" size={60} radius="md" onClick={copy}>
                                                            {copied ? <IconCheck size={28} /> : <IconCopy size={28} />}
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}
                                            </CopyButton>
                                        )}
                                    </Group>
                                </Stack>
                            </Paper>

                            <AnimatePresence>
                                {hasGenerated && (
                                    <Stack w="100%" gap={50}>

                                        {/* LIVE SIMULATOR */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 50 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, type: "spring" }}
                                        >
                                            <Box w="100%">
                                                <Group justify="space-between" align="end" mb="sm">
                                                    <Title order={3} c="white">Live Preview</Title>
                                                    <Group gap={5}>
                                                        <IconUsers size={16} color="gray" />
                                                        <Text size="sm" c="dimmed">{lobbyNames.length} Players Waiting...</Text>
                                                    </Group>
                                                </Group>

                                                <Paper
                                                    p="md" radius="lg"
                                                    style={{
                                                        backgroundColor: '#46178f',
                                                        backgroundImage: 'radial-gradient(circle at 50% 50%, #5d20bc 0%, #46178f 80%)',
                                                        minHeight: 200, position: 'relative', overflow: 'hidden', border: '4px solid #333'
                                                    }}
                                                >
                                                    <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(0,0,0,0.1)', borderRadius: '50%' }} />
                                                    <div style={{ position: 'absolute', bottom: -40, left: 10, width: 150, height: 150, background: 'rgba(0,0,0,0.1)', transform: 'rotate(45deg)' }} />

                                                    <Box bg="rgba(0,0,0,0.3)" p="xs" mb="lg" style={{ borderRadius: 8, textAlign: 'center', maxWidth: 200, margin: '0 auto 20px auto' }}>
                                                        <Text c="white" fw={800} size="sm">PIN: 69420</Text>
                                                    </Box>

                                                    <Group justify="center" gap="sm">
                                                        {lobbyNames.map((name, i) => (
                                                            <motion.div
                                                                key={`${name}-${i}`} // Ensure unique key for animation
                                                                initial={{ scale: 0, opacity: 0 }}
                                                                animate={{ scale: 1, opacity: 1 }}
                                                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                                            >
                                                                <Text
                                                                    fw={700} c="white" size="lg"
                                                                    style={{
                                                                        textShadow: '0 2px 0 rgba(0,0,0,0.2)', padding: '4px 12px',
                                                                        background: i === 0 ? 'rgba(255,255,255,0.2)' : 'transparent',
                                                                        borderRadius: 4
                                                                    }}
                                                                >
                                                                    {name}
                                                                </Text>
                                                            </motion.div>
                                                        ))}
                                                    </Group>
                                                </Paper>
                                            </Box>
                                        </motion.div>

                                        {/* STYLE VARIANTS (With Click Logic) */}
                                        <motion.div
                                            initial={{ opacity: 0, y: 50 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: 0.1, type: "spring" }}
                                        >
                                            <Stack w="100%" gap="md">
                                                <Group align="center" gap="xs">
                                                    <IconPalette color="#e64980" />
                                                    <Title order={3} c="white">Style Variants</Title>
                                                </Group>
                                                <Text c="dimmed">Click a style to copy AND preview it in the lobby.</Text>

                                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                                    {GLYPH_OPTIONS.map((opt) => {
                                                        const styledName = opt.transform(generatedName || '');
                                                        return (
                                                            <CopyButton key={opt.id} value={styledName} timeout={500}>
                                                                {({ copied, copy }) => (
                                                                    <Paper
                                                                        component="button"
                                                                        onClick={() => {
                                                                            copy();
                                                                            handleStyleClick(styledName);
                                                                        }}
                                                                        p="lg" radius="lg"
                                                                        withBorder
                                                                        style={{
                                                                            backgroundColor: copied ? 'rgba(32, 201, 151, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                                                            borderColor: copied ? '#20c997' : 'rgba(255, 255, 255, 0.1)',
                                                                            cursor: 'pointer',
                                                                            textAlign: 'left',
                                                                            transition: 'all 0.2s',
                                                                            width: '100%'
                                                                        }}
                                                                    >
                                                                        <Group justify="space-between" mb={5}>
                                                                            <Badge variant="dot" color={copied ? 'teal' : 'gray'} size="sm">{opt.label}</Badge>
                                                                            {copied && <IconCheck size={16} color="#20c997" />}
                                                                        </Group>
                                                                        <Text c="white" size="lg" fw={700} truncate>{styledName}</Text>
                                                                    </Paper>
                                                                )}
                                                            </CopyButton>
                                                        );
                                                    })}
                                                </SimpleGrid>
                                            </Stack>
                                        </motion.div>

                                    </Stack>
                                )}
                            </AnimatePresence>

                        </Stack>
                    </Container>
                </CyberBackground>
            </Box>

            <Box bg="dark.9" style={{ position: 'relative', zIndex: 20 }}>
                <FAQ customData={NAME_GEN_FAQ} />
            </Box>

            <Footer />
        </Box>
    );
}